// Header user menu (U5/U10/U12): profile, change password, company (portal
// users), shortcuts help, stop impersonation, logout.

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Avatar, Box, Divider, ListItemIcon, ListItemText, Menu, MenuItem, Typography } from '@mui/material';
import { Building, Keyboard, Lock1, Logout, Profile, UserRemove } from 'iconsax-reactjs';

import { t } from '../../../i18n';
import type { ShellUser } from '../api';
import { UI } from '../../../brand/ui';

export function UserMenu({
  user,
  onLogout,
  onStopImpersonation,
  onShowShortcuts
}: {
  user: ShellUser | null;
  onLogout: () => void;
  onStopImpersonation: () => void;
  onShowShortcuts: () => void;
}) {
  const navigate = useNavigate();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const name = user?.name ?? user?.username ?? t('common.signedInUser');
  const initials = name
    .split(/\s+/)
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
  const isPortal = Boolean(user?.customer_id || user?.supplier_id);

  const go = (to: string) => {
    setAnchor(null);
    navigate(to);
  };

  return (
    <>
      <Box
        component="button"
        type="button"
        data-testid="user-menu"
        onClick={(event) => setAnchor(event.currentTarget)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          border: `1px solid ${UI.border}`,
          bgcolor: UI.white,
          borderRadius: UI.radiusMd,
          pl: 0.5,
          pr: 1.25,
          py: 0.4,
          cursor: 'pointer',
          font: 'inherit',
          color: 'text.primary'
        }}
      >
        <Avatar src={user?.avatar ?? undefined} sx={{ width: 28, height: 28, fontSize: 12, bgcolor: 'primary.main' }}>
          {initials}
        </Avatar>
        <Typography variant="body2" sx={{ fontWeight: 700, maxWidth: 140, display: { xs: 'none', sm: 'block' } }} noWrap>
          {name}
        </Typography>
      </Box>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            {name}
          </Typography>
          {user?.email ? (
            <Typography variant="caption" color="text.secondary">
              {user.email}
            </Typography>
          ) : null}
        </Box>
        <Divider />
        <MenuItem onClick={() => go('/profile')}>
          <ListItemIcon>
            <Profile size={18} />
          </ListItemIcon>
          <ListItemText>{t('shell.profile')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => go('/profile?tab=password')}>
          <ListItemIcon>
            <Lock1 size={18} />
          </ListItemIcon>
          <ListItemText>{t('shell.changePassword')}</ListItemText>
        </MenuItem>
        {isPortal ? (
          <MenuItem onClick={() => go('/company')}>
            <ListItemIcon>
              <Building size={18} />
            </ListItemIcon>
            <ListItemText>{t('nav.company')}</ListItemText>
          </MenuItem>
        ) : null}
        <MenuItem
          onClick={() => {
            setAnchor(null);
            onShowShortcuts();
          }}
        >
          <ListItemIcon>
            <Keyboard size={18} />
          </ListItemIcon>
          <ListItemText>{t('shell.shortcuts')}</ListItemText>
        </MenuItem>
        {user?.acting_user ? (
          <MenuItem
            onClick={() => {
              setAnchor(null);
              onStopImpersonation();
            }}
          >
            <ListItemIcon>
              <UserRemove size={18} />
            </ListItemIcon>
            <ListItemText>{t('shell.impersonation.stop')}</ListItemText>
          </MenuItem>
        ) : null}
        <Divider />
        <MenuItem
          onClick={() => {
            setAnchor(null);
            onLogout();
          }}
        >
          <ListItemIcon>
            <Logout size={18} />
          </ListItemIcon>
          <ListItemText>{t('action.logout')}</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
}
